import React from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import dayjs from '@mobi/utils/date/extendedDayjs'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { TrustedDevicePrompt } from '@mobi/component-library/Login/TrustedDevicePrompt'
import { HasLoggedIn } from '@core/State/UserAccount/userAccountDriver'
import { useAppSelector } from '@core/Store/hooks'
import { AppRoutes } from '@core/AppRoutes'
import { getTrustedDeviceDetails } from '@core/Areas/Login/Store/selectors'
import { recordTrustedDevicePrompt, resetTrustedDevicePrompt } from '@core/Areas/Login/Store'
import { useTrustDevice } from '@core/Areas/Settings/Components/MFASettings/Hooks/useTrustDevice'
import { trackMfaEvent } from '@core/Areas/Settings/Components/MFASettings/Helpers/helpers'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import { useMfaDevices } from '@core/Areas/Settings/Components/MFASettings/Hooks/useMfaDevices'
import { MAX_TRUSTED_DEVICES } from '@core/Areas/Settings/Components/MFASettings/Components/MFADeviceGroup'
import { CognitoUnauthorizedError } from '@core/Areas/Settings/Components/MFASettings/Hooks/useCognitoUserIsAuthenticated'

export const TrustedDeviceTray = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const trustedDevices = useAppSelector(getTrustedDeviceDetails)
  const { lastSuccessfulLogin, trustedDeviceLastPrompted } = useAppSelector(state => state.login)
  const [isOpen, setIsOpen] = React.useState(false)
  const { trustDevice } = useTrustDevice()
  const [accountNumber, setAccountNumber] = React.useState<number | undefined>(undefined)

  const {
    isLoading: isDeviceListLoading,
    isError: isDeviceListError,
    data: deviceList,
  } = useMfaDevices(accountNumber)

  const displayedLessThan30DaysAgo = React.useMemo(
    () => dayjs().diff(dayjs(trustedDeviceLastPrompted), 'day') < 30,
    [trustedDeviceLastPrompted]
  )

  React.useEffect(() => {
    const subscription = HasLoggedIn.signal$.subscribe(({ data }) => {
      const { accountNumber } = data

      // Only display on home screen
      if (pathname !== AppRoutes.Home) return
      // Only display if last login used MFA
      if (!lastSuccessfulLogin?.withMfa) return
      // Only display if there are trusted devices
      if (trustedDevices[accountNumber] === undefined) return
      // Only display if user hasn't already 'trusted' their device
      if (trustedDevices[accountNumber].isRemembered) return
      // Only display if last prompt was more than 30 days ago
      if (displayedLessThan30DaysAgo) return

      setAccountNumber(accountNumber)
      setIsOpen(true)
    })
    return () => subscription?.dispose()
  }, [
    pathname,
    lastSuccessfulLogin,
    displayedLessThan30DaysAgo,
    trustedDevices,
    accountNumber,
    dispatch,
  ])

  // filter out devices with the last login more than 30 days ago, because these will
  // be automatically removed by the server so shouldn't count towards the limit
  const maxAgeForValidTrustedDeviceInDays = 30
  const millisecondsInDay = 1000 * 60 * 60 * 24
  const validTrustedDeviceCutoffDate =
    Date.now() - millisecondsInDay * maxAgeForValidTrustedDeviceInDays
  const validTrustedDevices = (deviceList?.devices ?? []).filter(
    d =>
      d.deviceLastAuthenticatedDate &&
      new Date(d.deviceLastAuthenticatedDate).getTime() > validTrustedDeviceCutoffDate
  )
  const hasReachedMaxTrustedDevices = validTrustedDevices.length >= MAX_TRUSTED_DEVICES

  const onTrustDevice = async () => {
    if (!deviceList?.devices) return

    // If the customer has already reached the max number of trusted devices,
    // then navigate them to the MFA settings page to manage their devices
    if (deviceList.devices.length >= MAX_TRUSTED_DEVICES) {
      setIsOpen(false)
      dispatch(recordTrustedDevicePrompt())
      await navigate('/account/settings/mfa?expand-mfa-devices')
      return
    }

    // Close the tray, and perform the trusted device update in the background
    // Provide success/failure feedback via a toast notification
    setIsOpen(false)

    if (accountNumber) {
      try {
        await trustDevice(accountNumber)
        dispatch(resetTrustedDevicePrompt())
        trackMfaEvent({ interaction_type: 'Trust this device', otpFlow: 'Login', skipped: false })
        addToast({
          id: 'trusted-device-success',
          title: 'Trusted device registered',
          subtitle:
            'You will be asked to enter a secure code less frequently when using this trusted device.',
          icon: 'success',
        })
      } catch (err) {
        if (err instanceof CognitoUnauthorizedError) {
          addToast({
            id: 'trusted-device-error',
            isDismissable: true,
            timeoutMs: Infinity,
            title: 'Trusted Devices',
            subtitle:
              'You must re-authenticate before you can trust this device. Please log in again.',
            icon: 'error',
          })
        } else {
          const subtitle = 'Adding your device was unsuccessful.'
          addToast({
            id: 'trusted-device-error',
            isDismissable: true,
            timeoutMs: Infinity,
            title: 'Trusted Devices',
            subtitle,
            button: {
              text: 'Manage',
              onClick: () => {
                removeToast({ id: 'trusted-device-error' })
                navigate('/account/settings/mfa')
              },
            },
            icon: 'error',
          })
        }
      }
    }
  }

  const onSkip = () => {
    setIsOpen(false)
    dispatch(recordTrustedDevicePrompt())
    trackMfaEvent({ interaction_type: 'Skip for now', otpFlow: 'Login', skipped: true })
  }

  const isTrustDeviceEnabled = !isDeviceListLoading && !isDeviceListError

  return (
    <Tray
      isOpen={isOpen}
      headerTitle={null}
      shouldCoverFullscreen={false}
      dismissStatus='disallowed'
    >
      <TrustedDevicePrompt
        isTrustDeviceEnabled={isTrustDeviceEnabled}
        onSkip={onSkip}
        onTrustDevice={onTrustDevice}
        primaryButtonText={hasReachedMaxTrustedDevices ? 'Manage Trusted Devices' : undefined}
      />
    </Tray>
  )
}
